import { GoogleGenerativeAI } from '@google/generative-ai';
import { Settings } from './js/settings.js';
import { ChatUI } from './js/chatUI.js';
import { ChatManager } from './js/chatManager.js';
import { HelpManager } from './js/helpManager.js';

// DOM Elements
const messageInput = document.getElementById('messageInput');
const sendButton = document.getElementById('sendButton');
const newChatButton = document.getElementById('newChatButton');
const chatMessages = document.getElementById('chatMessages');
const settingsBtn = document.getElementById('settingsBtn');
const settingsPanel = document.getElementById('settingsPanel');
const settingsOverlay = document.getElementById('settingsOverlay');
const themeToggle = document.getElementById('themeToggle');
const apiKeyInput = document.getElementById('apiKey');
const temperatureInput = document.getElementById('temperature');
const temperatureValue = document.getElementById('temperatureValue');
const systemPromptInput = document.getElementById('systemPrompt');
const testApiKeyBtn = document.getElementById('testApiKey');
const modelSelect = document.getElementById('modelSelect');

let chatSession = null;
let isProcessing = false;

// Initialize components
const settings = new Settings();
const chatUI = new ChatUI();
const chatManager = new ChatManager(chatUI);
const helpManager = new HelpManager();

// Event Listeners
document.addEventListener('DOMContentLoaded', () => {
    loadSettings();
    setupEventListeners();
});

function setupEventListeners() {
    // Chat input handlers
    messageInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    });

    sendButton.addEventListener('click', sendMessage);
    newChatButton.addEventListener('click', () => {
        chatMessages.innerHTML = '';
        chatSession = null;
    });

    // Help panel handlers
    const helpBtn = document.getElementById('helpBtn');
    const helpPanel = document.getElementById('helpPanel');
    const helpClose = document.getElementById('helpClose');
    const helpOverlay = document.getElementById('helpOverlay');

    helpBtn.onclick = () => {
        helpPanel.classList.remove('hidden');
        helpOverlay.classList.remove('hidden');
    };

    helpClose.onclick = () => {
        helpPanel.classList.add('hidden');
        helpOverlay.classList.add('hidden');
    };

    helpOverlay.onclick = () => {
        helpPanel.classList.add('hidden');
        helpOverlay.classList.add('hidden');
    };

    // Test API key
    testApiKeyBtn.onclick = async () => {
        const apiKey = apiKeyInput.value.trim();
        if (!apiKey) {
            alert('Please enter an API key first');
            return;
        }

        testApiKeyBtn.classList.add('loading');
        testApiKeyBtn.textContent = 'Testing...';
        testApiKeyBtn.disabled = true;

        try {
            // First test a simple generation to verify the API key
            const genAI = new GoogleGenerativeAI(apiKey);
            const model = genAI.getGenerativeModel({
                model: "gemini-2.5-flash",
            });
            await model.generateContent('Test message. Please respond with "OK" if you receive this.');

            // If successful, get available models
            const models = await listAvailableModels(apiKey);
            
            // Update model selector
            populateModelSelector(models);
            
            // Save models to localStorage
            localStorage.setItem('availableModels', JSON.stringify(models));
            localStorage.setItem('apiKey', apiKey);

            testApiKeyBtn.classList.remove('loading');
            testApiKeyBtn.classList.add('success');
            testApiKeyBtn.textContent = '✓ API Key Valid';

            // Reset button after 2 seconds
            setTimeout(() => {
                testApiKeyBtn.classList.remove('success');
                testApiKeyBtn.textContent = 'Test API Key';
                testApiKeyBtn.disabled = false;
            }, 2000);

        } catch (error) {
            console.error('API Key test failed:', error);
            testApiKeyBtn.classList.remove('loading');
            testApiKeyBtn.classList.add('error');
            testApiKeyBtn.textContent = '✕ Invalid API Key';

            // Reset button after 2 seconds
            setTimeout(() => {
                testApiKeyBtn.classList.remove('error');
                testApiKeyBtn.textContent = 'Test API Key';
                testApiKeyBtn.disabled = false;
            }, 2000);
        }
    };

    // Theme toggle
    themeToggle.onclick = () => {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
        themeToggle.innerHTML = `<i class="ri-${newTheme === 'light' ? 'sun' : 'moon'}-line"></i>`;
    };

    // Settings changes
    apiKeyInput.onchange = () => localStorage.setItem('apiKey', apiKeyInput.value);
    temperatureInput.oninput = (e) => {
        const value = e.target.value;
        temperatureValue.textContent = value;
        localStorage.setItem('temperature', value);
    };
    systemPromptInput.onchange = () => {
        localStorage.setItem('systemPrompt', systemPromptInput.value);
        const messageCount = chatMessages.querySelectorAll('.message:not(.welcome-message):not(.system-message)').length;
        if (messageCount > 0) {
            alert('Note: The new system prompt will only take effect when you start a new chat session. Click "New Chat" to start fresh with the new prompt.');
        }
    };
    modelSelect.onchange = () => {
        localStorage.setItem('selectedModel', modelSelect.value);
        const messageCount = chatMessages.querySelectorAll('.message:not(.welcome-message):not(.system-message)').length;
        if (messageCount > 0) {
            alert('Note: The new model selection will only take effect when you start a new chat session. Click "New Chat" to start fresh with the new model.');
        }
    };
}

async function listAvailableModels(apiKey) {
    const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models', {
        headers: {
            'x-goog-api-key': apiKey
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch models');
    }
    
    const data = await response.json();
    // Filter out embedding models and sort by displayName
    return data.models.filter(model => 
        !model.name.includes('embedding') && 
        model.supportedGenerationMethods.includes('generateContent')
    ).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function populateModelSelector(models) {
    const modelSelect = document.getElementById('modelSelect');
    modelSelect.innerHTML = ''; // Clear existing options
    
    models.forEach(model => {
        const option = document.createElement('option');
        // Extract the model name from the full path (e.g., "models/gemini-1.5-flash" -> "gemini-1.5-flash")
        option.value = model.name.replace('models/', '');
        option.textContent = model.displayName;
        modelSelect.appendChild(option);
    });

    // Set default to Flash if it exists
    const flashOption = Array.from(modelSelect.options).find(option => 
        option.value === 'gemini-2.5-flash'
    );
    if (flashOption) {
        flashOption.selected = true;
        localStorage.setItem('selectedModel', 'gemini-2.5-flash');
    }
}

function loadSettings() {
    const savedApiKey = localStorage.getItem('apiKey') || '';
    apiKeyInput.value = savedApiKey;

    const savedTemp = localStorage.getItem('temperature') || '1.0';
    temperatureInput.value = savedTemp;
    temperatureValue.textContent = savedTemp;

    const defaultPrompt = 'You are a super duper assistant';
    const savedPrompt = localStorage.getItem('systemPrompt') || defaultPrompt;
    systemPromptInput.value = savedPrompt;

    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
    themeToggle.innerHTML = `<i class="ri-${savedTheme === 'light' ? 'sun' : 'moon'}-line"></i>`;

    // Load saved models if they exist
    const savedModels = localStorage.getItem('availableModels');
    if (savedModels) {
        const models = JSON.parse(savedModels);
        populateModelSelector(models);
    }

    // Restore selected model if it exists
    const savedSelectedModel = localStorage.getItem('selectedModel');
    if (savedSelectedModel) {
        const modelSelect = document.getElementById('modelSelect');
        modelSelect.value = savedSelectedModel;
    }
}

async function initializeChat() {
    const apiKey = apiKeyInput.value;
    if (!apiKey) {
        throw new Error('Please enter your API key in settings (gear icon)');
    }

    const genAI = new GoogleGenerativeAI(apiKey);
    const selectedModel = modelSelect.value;
    const model = genAI.getGenerativeModel({
        model: selectedModel,
    });

    const generationConfig = {
        temperature: parseFloat(temperatureInput.value),
        topP: 0.95,
        topK: 40,
        maxOutputTokens: 8192,
    };

    return model.startChat({
        generationConfig,
        history: [
            {
                role: "user",
                parts: [{ text: "From now on, you must follow this system prompt: " + systemPromptInput.value }],
            },
            {
                role: "model",
                parts: [{ text: "I understand and will follow the system prompt: " + systemPromptInput.value }],
            }
        ],
    });
}

function addMessageToUI(role, content) {
    const messageDiv = document.createElement('div');
    messageDiv.className = `message ${role}-message`;
    messageDiv.textContent = content;
    chatMessages.appendChild(messageDiv);
    chatMessages.scrollTop = chatMessages.scrollHeight;
}

function addTypingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'message bot-message typing-indicator';
    indicator.innerHTML = '<span></span><span></span><span></span>';
    chatMessages.appendChild(indicator);
    chatMessages.scrollTop = chatMessages.scrollHeight;
    return indicator;
}

async function sendMessage() {
    if (isProcessing || !messageInput.value.trim()) return;

    const message = messageInput.value.trim();
    messageInput.value = '';

    addMessageToUI('user', message);

    try {
        isProcessing = true;
        sendButton.disabled = true;

        if (!chatSession) {
            chatSession = await initializeChat();
        }

        const typingIndicator = addTypingIndicator();
        const result = await chatSession.sendMessage(message);
        const response = result.response.text();
        
        typingIndicator.remove();
        addMessageToUI('bot', response);

    } catch (error) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'message error-message';
        errorDiv.textContent = `Error: ${error.message}`;
        chatMessages.appendChild(errorDiv);
    } finally {
        isProcessing = false;
        sendButton.disabled = false;
        chatMessages.scrollTop = chatMessages.scrollHeight;
    }
}
